import * as messagingApi from '@/services/api/providerMessaging.js';

/**
 * Resolves a provider-messaging participant's display name from first/last name fields.
 * Returns null (not the direct address) when unresolved, so callers can decide how to
 * fall back without ending up rendering the direct address twice.
 * @param {{first_name?: string, last_name?: string, organization?: string}} person
 * @returns {string|null}
 */
export function participantDisplayName(person) {
  const parts = [person?.first_name, person?.last_name].filter(Boolean);
  if (parts.length) return parts.join(' ');
  return person?.organization || null;
}

let addressBookPromise = null;

/**
 * Loads the unified address book (internal staff + saved contacts) once and caches it
 * as a Map keyed by lowercased direct_address. Shared across all callers so switching
 * between the inbox and a patient's message thread doesn't refetch.
 * @returns {Promise<Map<string, object>>}
 */
export function loadAddressBook() {
  if (!addressBookPromise) {
    addressBookPromise = messagingApi
      .listContacts()
      .then((entries) => {
        const map = new Map();
        for (const entry of entries) {
          if (entry.direct_address) map.set(entry.direct_address.toLowerCase(), entry);
        }
        return map;
      })
      .catch(() => new Map());
  }
  return addressBookPromise;
}

/**
 * Resolves a display name for a raw direct address using the cached address book.
 * Returns null (not the address) when unresolved.
 * @param {Map<string, object>|null} addressBook
 * @param {string} address
 * @returns {string|null}
 */
export function addressBookDisplayName(addressBook, address) {
  if (!addressBook || !address) return null;
  const entry = addressBook.get(address.toLowerCase());
  return entry ? participantDisplayName(entry) : null;
}
